import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { BotsProvider } from './contexts/BotsContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { Toaster } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import { VersionBadge } from './components/VersionBadge';

// Direct imports instead of lazy loading to avoid loading issues
import HomePage from './pages/HomePage';
import { StrategiesPage } from './pages/StrategiesPage';
import { PricingPage } from './pages/PricingPage';
import { FAQPage } from './pages/FAQPage';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { EmailConfirmedPage } from './pages/EmailConfirmedPage';
import { DashboardPage } from './pages/DashboardPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import CreateBotPage from './pages/CreateBotPage';
import { BotDetailsPage } from './pages/BotDetailsPage';
import LiveTradesPage from './pages/LiveTradesPage';
import ActiveBotsPage from './pages/ActiveBotsPage';
import BacktestsPage from './pages/BacktestsPage';
import BacktestDetailsPage from './pages/BacktestDetailsPage';
import ApiKeysPage from './pages/ApiKeysPage';
import { CoinsAmountsPage } from './pages/CoinsAmountsPage';
import MyBotsPage from './pages/MyBotsPage';
import BotStatsPage from './pages/BotStatsPage';
import SellBotPage from './pages/SellBotPage';
import MarketplacePage from './pages/MarketplacePage';
import SettingsPage from './pages/SettingsPage';

const ConditionalNavbar = () => {
  const location = useLocation();
  const dashboardRoutes = [
    '/dashboard',
    '/my-bots',
    '/admin',
    '/create-bot',
    '/create',
    '/bot',
    '/bots',
    '/edit',
    '/active-bots',
    '/backtests',
    '/backtest',
    '/coins-amounts',
    '/api-keys',
    '/live-trades',
    '/bot-stats',
    '/sell-bot',
    '/marketplace',
    '/settings'
  ];

  const isDashboardRoute = dashboardRoutes.some(route => location.pathname.startsWith(route));

  return isDashboardRoute ? null : <Navbar />;
};

const ConditionalFooter = () => {
  const location = useLocation();
  const dashboardRoutes = [
    '/dashboard',
    '/my-bots',
    '/admin',
    '/create-bot',
    '/create',
    '/bot',
    '/bots',
    '/edit',
    '/active-bots',
    '/backtests',
    '/backtest',
    '/coins-amounts',
    '/api-keys',
    '/live-trades',
    '/bot-stats',
    '/sell-bot',
    '/marketplace',
    '/settings'
  ];

  const isDashboardRoute = dashboardRoutes.some(route => location.pathname.startsWith(route));

  return isDashboardRoute ? null : <Footer />;
};

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <BotsProvider>
            <NotificationsProvider>
              <div className="flex flex-col min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
                <ConditionalNavbar />
                <ErrorBoundary>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/strategies" element={<StrategiesPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/signin" element={<SignInPage />} />
                    <Route path="/signup" element={<SignUpPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/verified" element={<EmailConfirmedPage />} />
                    <Route path="/confirm" element={<EmailConfirmedPage />} />
                    <Route path="/dashboard" element={<DashboardPage activeTab="dashboard" />} />
                    <Route path="/my-bots" element={<MyBotsPage />} />
                    <Route path="/admin/*" element={<AdminDashboardPage />} />
                    <Route path="/create-bot" element={<CreateBotPage />} />
                    <Route path="/create/:id" element={<CreateBotPage />} />
                    <Route path="/bot/:id" element={<BotDetailsPage />} />
                    <Route path="/bots/:id/edit" element={<CreateBotPage />} />
                    <Route path="/edit" element={<CreateBotPage />} />
                    <Route path="/active-bots" element={<ActiveBotsPage />} />
                    <Route path="/backtests" element={<BacktestsPage />} />
                    <Route path="/backtest/:id" element={<BacktestDetailsPage />} />
                    <Route path="/coins-amounts" element={<CoinsAmountsPage />} />
                    <Route path="/api-keys" element={<ApiKeysPage />} />
                    <Route path="/live-trades" element={<LiveTradesPage />} />
                    <Route path="/bot-stats" element={<BotStatsPage />} />
                    <Route path="/sell-bot" element={<SellBotPage />} />
                    <Route path="/marketplace" element={<MarketplacePage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Routes>
                </ErrorBoundary>
                <ConditionalFooter />
                <Toaster 
                  position="top-right"
                  expand
                  richColors
                  closeButton
                  theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                />
              </div>
            </NotificationsProvider>
          </BotsProvider>
        </AuthProvider>
      </ThemeProvider>
      <VersionBadge />
    </Router>
  );
}

export default App;